// SHIP + — variantId is set once in cart.liquid (window.OWShipPlus.variantId)
const insuranceProductID = String(window.OWShipPlus?.variantId || '');
let insuranceCheckChain = Promise.resolve();
let insuranceBusy = true;
let cachedInsuranceItem = null;

function rememberInsuranceFromCart(cart) {
    cachedInsuranceItem = cart ? getInsuranceItem(cart) : null;
}

async function getCart() {
    const response = await fetch(window.Shopify.routes.root + 'cart.js');
    return response.json();
}

function getInsuranceItem(cart) {
    return cart.items.find(i => Number(i.id) === Number(insuranceProductID));
}

function getRealCartItemCount(cart) {
    return cart.items.reduce((count, item) => {
        if (Number(item.id) === Number(insuranceProductID)) {
            return count;
        }

        return count + item.quantity;
    }, 0);
}

function getShipPlusProperties() {
    return window.__shipPlusScpProps || scpProps;
}

function hasInsurancePropertyChanges(item) {
    const shipPlusProperties = getShipPlusProperties();

    return Object.keys(shipPlusProperties).some((key) => {
        return String(item.properties?.[key]) !== String(shipPlusProperties[key]);
    });
}

async function insuranceInCart(cart) {
    try {
        const data = cart || await getCart();
        rememberInsuranceFromCart(data);
        const item = getInsuranceItem(data);
        const realItemCount = getRealCartItemCount(data);

        if (!item) {
            return { inCart: false, hasChanges: false, item: null, realItemCount };
        }

        return {
            inCart: true,
            hasChanges: hasInsurancePropertyChanges(item),
            item,
            realItemCount
        };
    } catch (error) {
        return { inCart: false, hasChanges: false, item: null, realItemCount: 0 };
    }
}

function setShipPlusLoading(loading) {
    window.OWCartPageAjax?.setSubtotalLoadingState?.(loading);
}

function setInsuranceToggleEnabled(enabled, options = {}) {
    const force = options.force === true;
    const allowInteraction = force || (enabled && !insuranceBusy);

    document.querySelectorAll('#insurance-toggle').forEach((toggle) => {
        toggle.disabled = !allowInteraction;
        toggle.setAttribute('aria-disabled', String(!allowInteraction));
    });
}

function setToggleChecked(checked) {
    document.querySelectorAll('#insurance-toggle').forEach((toggle) => {
        toggle.checked = checked;
    });
}

function getInsuranceIntent(cart) {
    const item = getInsuranceItem(cart);
    const realItemCount = getRealCartItemCount(cart);
    const inCart = Boolean(item);
    const hasChanges = item ? hasInsurancePropertyChanges(item) : false;
    const shipPlusDenied = sessionStorage.getItem('shipPlusDenied');

    if (inCart && realItemCount === 0) {
        return { willChange: true, targetChecked: false };
    }

    if ((!inCart || hasChanges) && (!shipPlusDenied || shipPlusDenied === 'false') && realItemCount > 0) {
        return { willChange: true, targetChecked: true };
    }

    return { willChange: false, targetChecked: inCart };
}

async function syncToggleToCart(cart) {
    const { inCart } = await insuranceInCart(cart);
    document.querySelectorAll('#insurance-toggle').forEach((toggle) => {
        toggle.checked = inCart;
    });
}

async function checkInsurance(options = {}) {
    const suppressUi = options.suppressUi === true;
    const { inCart, hasChanges, item, realItemCount } = await insuranceInCart(options.cart);
    const shipPlusDenied = sessionStorage.getItem('shipPlusDenied');

    if (inCart && realItemCount === 0) {
        return updateItemInCart(item.key, 0, true, { suppressUi });
    }

    if ((!inCart || hasChanges) && (!shipPlusDenied || shipPlusDenied === 'false') && realItemCount > 0) {
        return updateItemInCart(inCart ? item.key : insuranceProductID, 1, inCart, { suppressUi });
    }

    return false;
}

function runInsuranceCheck(options = {}) {
    const run = () => checkInsurance(options);
    insuranceCheckChain = insuranceCheckChain.then(run);
    return insuranceCheckChain;
}

function refreshCartAfterInsuranceChange(changed, cart, options = {}) {
    if (!changed) {
        return Promise.resolve();
    }

    const refresh = (cartPayload) => {
        if (window.OWCartPageAjax?.afterCartChange) {
            return window.OWCartPageAjax.afterCartChange(cartPayload, options);
        }

        window.location.reload();
    };

    if (cart) {
        return refresh(cart);
    }

    return getCart().then(refresh);
}

async function initInsuranceCheck() {
    if (!insuranceProductID) {
        insuranceBusy = false;
        setInsuranceToggleEnabled(true);
        return;
    }

    insuranceBusy = true;
    setInsuranceToggleEnabled(false);

    try {
        let cart = await getCart();
        rememberInsuranceFromCart(cart);

        const intent = getInsuranceIntent(cart);
        setToggleChecked(intent.targetChecked);

        if (intent.willChange) {
            setShipPlusLoading(true);
        }

        const changed = await checkInsurance({ suppressUi: true, cart });

        if (changed) {
            cart = await getCart();
            await refreshCartAfterInsuranceChange(true, cart, { insuranceOnly: true });
        }

        await syncToggleToCart(cart);
    } catch (error) {
        console.error('Ship+ check failed:', error);
        await syncToggleToCart();
    } finally {
        insuranceBusy = false;
        setShipPlusLoading(false);
        setInsuranceToggleEnabled(true, { force: true });
    }
}

function bootInsurance() {
    insuranceCheckChain = insuranceCheckChain.then(() => initInsuranceCheck());
}

document.addEventListener('DOMContentLoaded', bootInsurance);

async function handleInsurance() {
    await insuranceCheckChain;

    if (insuranceBusy || window.OWCartPageAjax?.isCartUpdating?.()) {
        await syncToggleToCart();
        return;
    }

    const toggle = document.querySelector('#insurance-toggle');
    if (!toggle || toggle.disabled) {
        return;
    }

    const wantsInsurance = toggle.checked;
    let inCart = Boolean(cachedInsuranceItem);
    let item = cachedInsuranceItem;

    insuranceBusy = true;
    setInsuranceToggleEnabled(false);
    setShipPlusLoading(true);

    try {
        if (!wantsInsurance && inCart && !item?.key) {
            const state = await insuranceInCart();
            item = state.item;
            inCart = state.inCart;
        }

        if (wantsInsurance === inCart) {
            toggle.checked = inCart;
            return;
        }

        if (wantsInsurance) {
            sessionStorage.setItem('shipPlusDenied', 'false');
            await updateItemInCart(insuranceProductID, 1);
        } else if (item?.key) {
            sessionStorage.setItem('shipPlusDenied', 'true');
            await updateItemInCart(item.key, 0, true);
        } else {
            toggle.checked = false;
        }
    } catch (error) {
        console.error('Ship+ toggle failed:', error);
        await syncToggleToCart();
    } finally {
        insuranceBusy = false;
        setInsuranceToggleEnabled(true);

        if (!window.OWCartPageAjax?.isCartUpdating?.()) {
            setShipPlusLoading(false);
        }
    }
}

async function updateItemInCart(productID, quantity, inCart, options = {}) {
    const suppressUi = options.suppressUi === true;

    if (suppressUi) {
        await postCartItemUpdate(productID, quantity, inCart);
        return true;
    }

    const doUpdate = async () => {
        await postCartItemUpdate(productID, quantity, inCart);
        const cart = await getCart();
        rememberInsuranceFromCart(cart);
        return cart;
    };

    if (window.OWCartPageAjax?.runCartUpdate) {
        await window.OWCartPageAjax.runCartUpdate(doUpdate, { insuranceOnly: true });
        return true;
    }

    const cart = await doUpdate();
    await refreshCartAfterInsuranceChange(true);
    return true;
}

async function postCartItemUpdate(productID, quantity, inCart) {
    let urlEndpoint = 'cart/add.js';
    if (quantity === 0 || inCart) {
        urlEndpoint = 'cart/change.js';
    }

    const requestBody = {
        id: productID,
        quantity
    };

    if (quantity > 0) {
        requestBody.properties = getShipPlusProperties();
    }

    const response = await fetch(window.Shopify.routes.root + urlEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
    });

    return response.json();
}

window.OWShipPlus = window.OWShipPlus || {};
window.OWShipPlus.getRealCartItemCount = getRealCartItemCount;
window.OWShipPlus.getInsuranceItem = getInsuranceItem;
window.OWShipPlus.runInsuranceCheck = runInsuranceCheck;
window.OWShipPlus.setToggleEnabled = function (enabled, options) {
    setInsuranceToggleEnabled(enabled, options);
};
window.OWShipPlus.isInsuranceBusy = () => insuranceBusy;
window.OWShipPlus.rememberCart = rememberInsuranceFromCart;